import { useMemo } from 'react';
import { compact, noop } from 'lodash';

import { DoctorSimple } from '@dreryk/egabinet-personnel-frontend-api';
import {
   VisitAndAppointmentPrescriptionOnlyView,
   VisitAndAppointmentPrescriptionOnlyViewQuery,
} from '@dreryk/egabinet-visit-frontend-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { convertDateToLocalDate, convertLocalDateTimeToDate, dateFormatter, Ordering } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   HorizontalAlign,
   JustifyContent,
   Padding,
   TableColumnConfiguration,
} from '@dreryk/react-components';
import { Range, Slot } from '@dreryk/watermelon';

import { createMedicalRoleVisitor, useCurrentPractitionerGlobalState } from '~libs.shared.account';
import { protectFeature } from '~libs.shared.system';
import { PatientGroups } from '~libs.desktop.shared.patient-groups';
import { PatientTableCellPresenter } from '~libs.desktop.shared.patient';
import { PractitionerSafePresenter } from '~libs.desktop.shared.practitioner-safe-presenter';
import { PatientEntitlementToMedicalServiceStatusIcon } from '~libs.desktop.patient-entitlement-to-medical-service';
import { AppointmentNoteCell } from '~libs.desktop.shared.appointment';

import { prescriptionVisitsTranslationKeys } from '../../translations';
import { MedicalEventStatusIcon } from '../../medicalEvents/MedicalEventStatusIcon';
import { StatusCell } from '../../appointment/table/cells/StatusCell';
import { useAppointmentsFiltersContext } from '../../appointment/filters/AppointmentFilter.context';

import { PrescriptionVisitAdditionalOptionsCell } from './cells/AdditionalOptions/PrescriptionVisitAdditionalOptionsCell';
import { PrescriptionVisitOptionsCell } from './cells/PrescriptionVisitOptionsCell';
import { PrescriptionVisitSettlementCell } from './cells/PrescriptionVisitSettlementCell';
import { isPrescriptionVisitSettlementAvailable } from './settlement/isPrescriptionVisitSettlementAvailable';
import { resolvePractitionerIdsQueryValue } from './PrescriptionVisitsTable.utils';

export const usePrescriptionVisitsDataConfiguration = (
   currentDoctor: DoctorSimple | null,
   doctors: DoctorSimple[]
): LazyLoadedDataConfigurationByCqrsQuery<VisitAndAppointmentPrescriptionOnlyView> => {
   const { getFilter } = useAppointmentsFiltersContext();
   const dateFilter = getFilter('date');
   const practitionerIds = getFilter('practitioner');
   const visitAndAppointmentStatuses = getFilter('visitAndAppointmentStatus');
   const patientGroupsIds = getFilter('patientGroup');

   const { getCurrentPractitioner } = useCurrentPractitionerGlobalState();
   const currentPractitioner = getCurrentPractitioner();
   const isReceptionist = protectFeature('updatingReceptionistPersonalData');

   const applyCurrentDoctorFilter =
      !isReceptionist &&
      createMedicalRoleVisitor(currentPractitioner)<boolean>({
         visitDoctor: () => true,
         visitParamedic: () => false,
         visitUnknown: () => false,
      });

   const practitionerIdsQueryValue = resolvePractitionerIdsQueryValue(
      practitionerIds,
      currentDoctor,
      doctors,
      applyCurrentDoctorFilter
   );

   const orderings: Ordering<VisitAndAppointmentPrescriptionOnlyView>[] = useMemo(
      () => [{ property: 'appointmentDate', descending: !dateFilter }],
      [dateFilter]
   );

   return useLazyLoadedDataConfigurationByCqrsQuery<VisitAndAppointmentPrescriptionOnlyView>(() => {
      const query = new VisitAndAppointmentPrescriptionOnlyViewQuery();

      if (dateFilter?.range) {
         const lower = convertDateToLocalDate(dateFilter.range.lowerEndpoint());
         const upper = convertDateToLocalDate(dateFilter.range.upperEndpoint());
         query.appointmentDateRange = Range.closed(lower, upper);
      }

      query.practitionerIds = Slot.of(practitionerIdsQueryValue);
      query.statuses = Slot.emptyIfNull(visitAndAppointmentStatuses.length ? visitAndAppointmentStatuses : null);
      query.patientGroupsIds = Slot.emptyIfNull(patientGroupsIds.length ? patientGroupsIds : null);

      return query;
   }, orderings);
};

// eslint-disable-next-line max-lines-per-function
export const usePrescriptionVisitsTableColumns = (
   refetchPrescriptionVisits: VoidFunction,
   onlinePaymentsIntegrationExists: boolean
): TableColumnConfiguration<VisitAndAppointmentPrescriptionOnlyView>[] => {
   const isSettlementAvailable = isPrescriptionVisitSettlementAvailable();

   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnsWidth = createDynamicColumnsWidth({
         appointmentDate: '110px',
         note: '32px',
         patientGroups: '22px',
         patient: isSettlementAvailable ? '22%' : '34%',
         practitioner: isSettlementAvailable ? '23%' : '33%',
         entitlementToMedicalService: '52px',
         settlement: isSettlementAvailable ? '33%' : '0px',
         status: isSettlementAvailable ? '22%' : '33%',
         medicalEvents: '52px',
         options: '122px',
         additionalOptions: '24px',
      });

      return compact([
         {
            accessor: 'appointmentDate',
            label: prescriptionVisitsTranslationKeys.datetime,
            width: columnsWidth.appointmentDate,
            formatter: dateFormatter,
         },
         {
            name: 'patientGroups',
            accessor: 'patientId',
            width: columnsWidth.patientGroups,
            sortingDisabled: true,
            justifyContent: JustifyContent.CENTER,
            padding: Padding.VERTICAL,
            renderer: (props) => {
               return <PatientGroups patientGroups={props.bean.patientGroups} direction={'column'} />;
            },
         },
         {
            name: 'patient',
            accessor: 'patientId',
            label: prescriptionVisitsTranslationKeys.patient,
            width: columnsWidth.patient,
            renderer: (props) => {
               return <PatientTableCellPresenter patient={props.bean} />;
            },
         },
         {
            name: 'practitioner',
            accessor: 'practitioner',
            label: prescriptionVisitsTranslationKeys.specialist,
            width: columnsWidth.practitioner,
            renderer: (props) => {
               return (
                  <PractitionerSafePresenter
                     practitioner={props.bean.practitioner}
                     testId={'practitioner'}
                     presentationOrder={'lastName'}
                     type={'standalone'}
                     withIdentification
                  />
               );
            },
         },
         {
            name: 'entitlementToMedicalService',
            accessor: 'entitlementToMedicalService',
            label: prescriptionVisitsTranslationKeys.entitlementToMedicalService,
            width: columnsWidth.entitlementToMedicalService,
            padding: Padding.NONE,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,
            sortingDisabled: true,
            renderer: ({ bean }) => (
               <PatientEntitlementToMedicalServiceStatusIcon
                  patientId={bean.patientId.asNumber()}
                  visitId={bean.visitId?.asNumber()}
                  entitlementToMedicalService={bean.entitlementToMedicalService}
                  checkDate={convertLocalDateTimeToDate(bean.appointmentDate.atStartOfDay())}
                  patientReferenceIdentifier={bean.patientReferenceIdentifier}
                  readonly={false}
               />
            ),
         },
         {
            name: 'note',
            accessor: 'note',
            width: columnsWidth.note,
            padding: Padding.NONE,
            justifyContent: JustifyContent.CENTER,
            sortingDisabled: true,
            renderer: ({ bean }) =>
               bean.note ? (
                  <AppointmentNoteCell
                     appointmentId={bean.appointmentId.asNumber()}
                     note={bean.note}
                     onNoteUpdated={refetchPrescriptionVisits}
                  />
               ) : null,
         },
         isSettlementAvailable && {
            name: 'settlement',
            accessor: noop,
            label: prescriptionVisitsTranslationKeys.settlement.label,
            width: columnsWidth.settlement,
            sortingDisabled: true,
            renderer: ({ bean }) => (
               <PrescriptionVisitSettlementCell
                  appointmentId={bean.appointmentId.asNumber()}
                  onlinePaymentsIntegrationExists={onlinePaymentsIntegrationExists}
               />
            ),
         },
         {
            name: 'status',
            accessor: 'status',
            label: prescriptionVisitsTranslationKeys.status,
            width: columnsWidth.status,
            renderer: ({ bean }) => <StatusCell status={bean.status} />,
         },
         {
            name: 'medicalEvents',
            accessor: noop,
            label: prescriptionVisitsTranslationKeys.medicalEvents,
            width: columnsWidth.medicalEvents,
            padding: Padding.NONE,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,
            sortingDisabled: true,
            renderer: ({ bean }) =>
               bean.visitMedicalEventStatus ? (
                  <MedicalEventStatusIcon
                     visitMedicalEventStatus={bean.visitMedicalEventStatus}
                     testId={'medicalEvent'}
                  />
               ) : null,
         },
         {
            name: 'options',
            accessor: noop,
            label: prescriptionVisitsTranslationKeys.options,
            width: columnsWidth.options,
            sortingDisabled: true,
            renderer: ({ bean }) => (
               <PrescriptionVisitOptionsCell bean={bean} refetchPrescriptionVisits={refetchPrescriptionVisits} />
            ),
         },
         {
            name: 'additionalOptions',
            width: columnsWidth.additionalOptions,
            accessor: noop,
            padding: Padding.NONE,
            sortingDisabled: true,
            renderer: ({ bean }) => (
               <PrescriptionVisitAdditionalOptionsCell
                  cancellable={bean.cancellable}
                  appointmentId={bean.appointmentId}
                  visitId={bean.visitId}
                  status={bean.status}
                  patientId={bean.patientId}
                  ownedByCurrentUser={bean.ownedByCurrentUser}
                  reopened={bean.reopened}
                  practitioner={bean.practitioner}
                  appointmentDate={bean.appointmentDate}
                  patientFirstName={bean.patientFirstName}
                  patientLastName={bean.patientLastName}
                  note={bean.note}
                  chronicDiseaseExists={bean.chronicDiseaseExists}
                  refetchPrescriptionVisits={refetchPrescriptionVisits}
                  onlinePaymentsIntegrationExists={onlinePaymentsIntegrationExists}
               />
            ),
         },
      ]);
   }, [refetchPrescriptionVisits, onlinePaymentsIntegrationExists, isSettlementAvailable]);
};
